import type { DayHours } from "./types";

// Date and time helpers, ported from the prototype. All slot math is in
// "minutes from midnight" to stay timezone agnostic on the client; the server
// builds real timestamps against the shop timezone for the calendar invite.

export const pad = (n: number): string => String(n).padStart(2, "0");

// Local-date to YYYY-MM-DD. Uses local fields so the calendar matches what the
// customer sees, never a UTC shift.
export const ymd = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const parseYmd = (s: string): Date => {
  const [y, m, da] = s.split("-").map(Number);
  return new Date(y, m - 1, da);
};

export const minutesToLabel = (mins: number): string => {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${pad(m)} ${ap}`;
};

export interface Slot {
  start: number;
  taken: boolean;
}

export interface BusyRange {
  start: number;
  end: number;
}

// A candidate range [cs, ce) conflicts with an existing busy range [bs, be) when
// they overlap OR sit closer together than the cooldown gap. Equivalent to
// overlapping the busy range after padding it by cooldown on both sides.
export const conflictsWithBusy = (
  cs: number, ce: number, bs: number, be: number, cooldown: number,
): boolean => cs < be + cooldown && bs - cooldown < ce;

// Generate slots for a day from its hours and a slot length, dropping any slot
// that would run past closing. A slot is "taken" when its range overlaps an
// existing appointment or falls within the cooldown gap around one (one groomer,
// one dog at a time, with cleanup time between, regardless of breed).
export const slotsForDay = (
  cfg: DayHours | undefined,
  slotMinutes: number,
  busy: BusyRange[],
  cooldownMinutes = 0,
): Slot[] => {
  if (!cfg?.open || slotMinutes <= 0) return [];
  const cooldown = Math.max(0, cooldownMinutes);
  const out: Slot[] = [];
  for (let t = cfg.start; t + slotMinutes <= cfg.end; t += slotMinutes) {
    const taken = busy.some((b) => conflictsWithBusy(t, t + slotMinutes, b.start, b.end, cooldown));
    out.push({ start: t, taken });
  }
  return out;
};

// 42-cell month grid (6 weeks) starting on the Sunday on or before the 1st.
export const monthGrid = (cursor: Date): Date[] => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

// Time options for admin hour selects: 6:00 AM to 10:00 PM in 30-min steps.
export const timeOptions = (): number[] => {
  const o: number[] = [];
  for (let t = 360; t <= 1320; t += 30) o.push(t);
  return o;
};
